import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";

const translations = {
  en: {
    "whatsapp.package.helsinki": "Hi, I'm interested in the Helsinki Package (€500) - includes 6 hours moving, 3 hours help, and 4 hours cleaning",
    "whatsapp.package.outside": "Hi, I'm interested in the Outside Helsinki Package (€800) - includes 10 hours moving, 3 hours help, and 4 hours cleaning",
    "whatsapp.package.moveout": "Hi, I'm interested in the Move-out Package (€250) - complete cleaning from start to finish",
    "whatsapp.package.balcony": "Hi, I'm interested in the Balcony & Window Cleaning Combo (€89)",
  },
  fi: {
    "whatsapp.package.helsinki.fi": "Hei, olen kiinnostunut Helsinki-paketista (500€) - sisältää 6 tuntia muuttoa, 3 tuntia apua ja 4 tuntia siivousta",
    "whatsapp.package.outside.fi": "Hei, olen kiinnostunut Helsinki ulkopuolisesta paketista (800€) - sisältää 10 tuntia muuttoa, 3 tuntia apua ja 4 tuntia siivousta",
    "whatsapp.package.moveout.fi": "Hei, olen kiinnostunut poismuuttopaketista (250€) - täydellinen siivous alusta loppuun",
    "whatsapp.package.balcony.fi": "Hei, olen kiinnostunut parveke- ja ikkunanpesukombo-paketista (89€)",
  },
};

const PricingSection = () => {
  const { language } = useLanguage();
  
  const packages = language === "en" ? [
    {
      title: "Helsinki Package",
      price: "€500",
      description: "Includes 6 hours of moving time in Helsinki",
      features: [
        "3 hours of moving help (packing and unpacking)",
        "Old or new home cleaning",
        "4 hours of cleaning"
      ]
    },
    {
      title: "Outside Helsinki Package",
      price: "€800",
      description: "Includes 10 hours of moving time (up to 300 km)",
      features: [
        "3 hours of moving help (packing and unpacking)",
        "Old or new home cleaning",
        "4 hours of cleaning"
      ]
    },
    {
      title: "Move-out Package",
      price: "€250",
      description: "Complete cleaning from start to finish",
      features: [
        "+20 € for cleaning supplies",
        "No extra charges — even if it takes longer!"
      ]
    },
    {
      title: "Balcony & Window Cleaning Combo",
      price: "€89",
      description: "Professional balcony and window cleaning service",
      features: [
        "Includes all balcony glass panels",
        "Additional windows available at 15€ each",
        "Enjoy sparkling clean views with professional care at a great price!"
      ]
    }
  ] : [
    {
      title: "Helsinki-paketti",
      price: "500€",
      description: "Sisältää 6 tuntia muuttoaikaa Helsingissä",
      features: [
        "3 tuntia muuttoapua (pakkaus ja purku)",
        "Vanhan tai uuden kotisi siivous",
        "4 tunnin siivous"
      ]
    },
    {
      title: "Helsinki ulkopuolinen paketti",
      price: "800€",
      description: "Sisältää 10 tuntia muuttoaikaa (jopa 300 km)",
      features: [
        "3 tuntia muuttoapua (pakkaus ja purku)",
        "Vanhan tai uuden kotisi siivous",
        "4 tunnin siivous"
      ]
    },
    {
      title: "Poismuuttopaketti",
      price: "250€",
      description: "Täydellinen siivous alusta loppuun",
      features: [
        "+20 € siivoustarvikkeista",
        "Ei lisämaksuja — vaikka kestäisi kauemmin!"
      ]
    },
    {
      title: "Parveke- ja ikkunanpesukombo",
      price: "89€",
      description: "Ammattitaitoinen parveke- ja ikkunanpesupalvelu",
      features: [
        "Sisältää kaikki parvekkeen lasilevyt",
        "Lisäikkunat saatavilla 15€/kpl",
        "Nauti kristallinkirkkaista näkymistä ammattitaidolla loistavaan hintaan!"
      ]
    }
  ];

  return (
    <section id="prices" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-2xl font-bold text-accent mb-4">
            {language === "en" ? "Best Offer" : "Paras tarjous"}
          </h2>
          <h3 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            {language === "en" ? "Premium service, excellent prices" : "Premium-palvelu, erinomaiset hinnat"}
          </h3>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            {language === "en"
              ? "ServiceMind24 offers the most affordable moving, cleaning, repair and maintenance services in Helsinki. We're proud to offer top quality at the best prices with no hidden fees."
              : "ServiceMind24 tarjoaa edullisimmat muutto-, siivous-, korjaus- ja huoltopalvelut Helsingissä. Olemme ylpeitä siitä, että voimme tarjota huippulaatua edullisimpiin hintoihin ilman piilomaksuja."
            }
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {packages.map((pkg, index) => (
            <Card key={index} className="border-2 border-border hover:border-accent transition-colors">
              <CardHeader className="text-center">
                <CardTitle className="text-xl font-bold text-primary mb-2">
                  {pkg.title}
                </CardTitle>
                <div className="text-3xl font-bold text-accent mb-2">
                  {pkg.price}
                </div>
                <p className="text-sm text-muted-foreground">
                  {pkg.description}
                </p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-6">
                  {pkg.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-2 text-sm">
                      <span className="w-1.5 h-1.5 bg-accent rounded-full mt-2"></span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className="w-full bg-primary hover:bg-primary-dark text-white whitespace-nowrap"
                  onClick={() => {
                    const packageKeys = language === "en" 
                      ? ["whatsapp.package.helsinki", "whatsapp.package.outside", "whatsapp.package.moveout", "whatsapp.package.balcony"]
                      : ["whatsapp.package.helsinki.fi", "whatsapp.package.outside.fi", "whatsapp.package.moveout.fi", "whatsapp.package.balcony.fi"];
                    const message = translations[language][packageKeys[index]];
                    window.open(`https://wa.me/358402401909?text=${encodeURIComponent(message)}`, '_blank');
                  }}
                >
                  {language === "en" ? "Book now" : "Varaa nyt"}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
